import { Label } from './ui/label';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Repeat } from 'lucide-react';

export const emptyRecurring = {
  enabled: false,
  interval_unit: 'months',
  interval_value: 1,
  start_date: new Date().toISOString().split('T')[0],
  end_mode: 'never',
  end_date: '',
  max_occurrences: 12,
  add_period_text: true,
};

const PRESETS = [
  { key: 'months-1', label: 'Monatlich', unit: 'months', value: 1 },
  { key: 'months-3', label: 'Vierteljährlich', unit: 'months', value: 3 },
  { key: 'months-6', label: 'Halbjährlich', unit: 'months', value: 6 },
  { key: 'years-1', label: 'Jährlich', unit: 'years', value: 1 },
  { key: 'weeks-1', label: 'Wöchentlich', unit: 'weeks', value: 1 },
  { key: 'weeks-2', label: 'Alle 2 Wochen', unit: 'weeks', value: 2 },
  { key: 'custom', label: 'Eigenes Intervall…', unit: null, value: null },
];

const presetKey = (r) => {
  const found = PRESETS.find((p) => p.unit === r.interval_unit && p.value === Number(r.interval_value));
  return found ? found.key : 'custom';
};

export function RecurringSettings({ value, onChange, showToggle = true, startLabel = 'Erste Rechnung am' }) {
  const r = value;
  const set = (patch) => onChange({ ...r, ...patch });
  const key = presetKey(r);

  return (
    <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 p-4 space-y-4" data-testid="recurring-settings">
      {showToggle && (
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Repeat className="w-4 h-4 text-violet-400 mt-0.5" />
            <div>
              <Label className="text-sm">Als Dauerrechnung einrichten</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Die Rechnung wird im gewählten Abstand automatisch erstellt und per E-Mail an den Kunden versendet.
              </p>
            </div>
          </div>
          <Switch
            checked={r.enabled}
            onCheckedChange={(v) => set({ enabled: v })}
            data-testid="recurring-toggle"
          />
        </div>
      )}

      {(r.enabled || !showToggle) && (
        <div className="space-y-4" data-testid="recurring-fields">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Intervall</Label>
              <Select
                value={key}
                onValueChange={(v) => {
                  const p = PRESETS.find((x) => x.key === v);
                  if (p?.unit) set({ interval_unit: p.unit, interval_value: p.value });
                  else set({ interval_unit: 'days', interval_value: 30 });
                }}
              >
                <SelectTrigger className="bg-background h-10" data-testid="recurring-interval-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{startLabel}</Label>
              <Input
                type="date"
                value={r.start_date}
                onChange={(e) => set({ start_date: e.target.value })}
                className="bg-background h-10"
                data-testid="recurring-start-input"
              />
            </div>
          </div>

          {key === 'custom' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="recurring-custom-interval">
              <div className="space-y-2">
                <Label className="text-xs">Alle</Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={r.interval_value}
                  onChange={(e) => set({ interval_value: Number(e.target.value) })}
                  className="bg-background h-10"
                  data-testid="recurring-interval-value-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Einheit</Label>
                <Select value={r.interval_unit} onValueChange={(v) => set({ interval_unit: v })}>
                  <SelectTrigger className="bg-background h-10" data-testid="recurring-interval-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">Tage</SelectItem>
                    <SelectItem value="weeks">Wochen</SelectItem>
                    <SelectItem value="months">Monate</SelectItem>
                    <SelectItem value="years">Jahre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Laufzeit</Label>
              <Select value={r.end_mode} onValueChange={(v) => set({ end_mode: v })}>
                <SelectTrigger className="bg-background h-10" data-testid="recurring-end-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Unbegrenzt – bis ich sie stoppe</SelectItem>
                  <SelectItem value="date">Bis zu einem Enddatum</SelectItem>
                  <SelectItem value="count">Feste Anzahl Rechnungen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {r.end_mode === 'date' && (
              <div className="space-y-2">
                <Label className="text-xs">Enddatum</Label>
                <Input
                  type="date"
                  value={r.end_date || ''}
                  onChange={(e) => set({ end_date: e.target.value })}
                  className="bg-background h-10"
                  data-testid="recurring-end-date-input"
                />
              </div>
            )}
            {r.end_mode === 'count' && (
              <div className="space-y-2">
                <Label className="text-xs">Anzahl Rechnungen</Label>
                <Input
                  type="number"
                  min={1}
                  max={240}
                  value={r.max_occurrences || 1}
                  onChange={(e) => set({ max_occurrences: Number(e.target.value) })}
                  className="bg-background h-10"
                  data-testid="recurring-count-input"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-4">
            <Label className="text-xs text-muted-foreground">
              Leistungszeitraum automatisch im Rechnungstext ergänzen (z.B. „Leistungszeitraum: Juni 2026“)
            </Label>
            <Switch
              checked={r.add_period_text}
              onCheckedChange={(v) => set({ add_period_text: v })}
              data-testid="recurring-period-text-toggle"
            />
          </div>
        </div>
      )}
    </div>
  );
}
